import _ from 'lodash';
import moment from 'moment';
import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  Comment,
  ConversationAPI,
  GetConversationsResponse,
  UserMessagePartial,
  WebsocketConversationMetaData,
} from 'api/conversations';
import {
  EventType,
  ObjectType,
  Action,
  SocketData,
  AccountSocketData,
} from 'api/pushes';
import { ConversationContext } from 'data/conversation';
import { useConversationReadReceiptSocketUpdate } from 'queries/project-global-conversation/read-receipt-ws';
import { useProjectWSUpdates } from './use-project-ws-updates';
import { useUser } from './use-user';
import { useUserWsUpdates } from './use-user-ws-updates';

const getConversationsKey = (context: ConversationContext | undefined) => [
  'conversations',
  context,
];

export const useGetConversationsForContext = (
  projectId: number,
  context: ConversationContext | undefined,
) => {
  return useQuery(
    getConversationsKey(context),
    () =>
      context ? ConversationAPI.getComments(projectId, context) : undefined,
    { keepPreviousData: true, enabled: !!context && !!projectId },
  );
};

export const useCreateComment = () => {
  const queryClient = useQueryClient();
  const userData = useUser();

  return useMutation(
    (args: {
      projectId: number;
      context: ConversationContext;
      comment: UserMessagePartial;
    }) => {
      queryClient.setQueryData<GetConversationsResponse>(
        getConversationsKey(args.context),
        (prevData) => {
          if (prevData) {
            return {
              comments: prevData?.comments?.concat({
                ...args.comment,
                commentContext: args.context,
                createdAt: moment().valueOf(),
                createdBy: userData?.user?.userId,
              } as unknown as Comment),
              members: prevData?.members,
            } as GetConversationsResponse;
          }

          return { comments: [], members: [] };
        },
      );

      return ConversationAPI.createComment(
        args?.projectId,
        args?.context,
        args?.comment,
      );
    },
    {
      onSuccess: (_data, variables) => {
        queryClient.refetchQueries(getConversationsKey(variables.context));
      },
    },
  );
};

export const useUpdateComment = () => {
  const queryClient = useQueryClient();

  return useMutation(
    (args: {
      projectId: number;
      context: ConversationContext;
      commentId: number;
      comment: UserMessagePartial;
    }) =>
      ConversationAPI.updateComment(
        args?.projectId,
        args?.context,
        args?.commentId,
        args?.comment,
      ),
    {
      onSuccess: (_data, args) => {
        queryClient.refetchQueries(getConversationsKey(args.context));
      },
    },
  );
};

export const useDeleteComment = () => {
  const queryClient = useQueryClient();

  return useMutation(
    (args: {
      projectId: number;
      context: ConversationContext;
      commentId: number;
    }) =>
      ConversationAPI.deleteComment(
        args?.projectId,
        args?.context,
        args?.commentId,
      ),
    {
      onSuccess: (_data, args) => {
        queryClient.refetchQueries(getConversationsKey(args.context));
      },
    },
  );
};

export const useUpdateLastSeen = () => {
  const queryClient = useQueryClient();

  return useMutation(
    (args: {
      context: ConversationContext;
      members: GetConversationsResponse['members'];
    }) => {
      const { context, members } = args;
      const data = queryClient.getQueryData<GetConversationsResponse>(
        getConversationsKey(context),
      );

      if (data) {
        queryClient.setQueryData<GetConversationsResponse>(
          getConversationsKey(context),
          (prevData) =>
            ({
              comments: prevData?.comments,
              members,
            } as GetConversationsResponse),
        );
      }

      return Promise.resolve();
    },
  );
};

export const useGetConversationWithWS = (
  projectId: number,
  context: ConversationContext | undefined,
) => {
  const query = useGetConversationsForContext(projectId, context);
  const queryClient = useQueryClient();

  useConversationReadReceiptSocketUpdate(projectId);

  const handleSocketUpdates = useCallback(
    (
      socketData:
        | SocketData<Comment & WebsocketConversationMetaData>
        | AccountSocketData<Comment & WebsocketConversationMetaData>,
    ) => {
      const { id, type } = socketData?.data?.commentContext ?? {};

      const commentContext = { id, type };

      if (_.isEqual(context, commentContext)) {
        query.refetch();
      }
    },
    [context, query],
  );

  const handleCommentSocketUpdates = useCallback(
    (socketData: SocketData<Comment & WebsocketConversationMetaData>) => {
      if (socketData?.objectType === ObjectType.Comment) {
        const commentContext = socketData?.data?.commentContext;

        queryClient.refetchQueries(getConversationsKey(commentContext));
      }
    },
    [queryClient],
  );

  useProjectWSUpdates(
    projectId,
    handleSocketUpdates,
    EventType(Action.Create, ObjectType.ConversationComment),
  );

  useProjectWSUpdates(
    projectId,
    handleSocketUpdates,
    EventType(Action.Update, ObjectType.ConversationComment),
  );

  useProjectWSUpdates(
    projectId,
    handleCommentSocketUpdates,
    EventType(Action.Update, ObjectType.Comment),
  );

  useProjectWSUpdates(
    projectId,
    handleSocketUpdates,
    EventType(Action.Delete, ObjectType.ConversationComment),
  );

  useUserWsUpdates<Comment & WebsocketConversationMetaData>(
    handleSocketUpdates,
    EventType(Action.Create, ObjectType.ResourceRequest),
  );

  return query;
};
